/**
 * Checks if a user has at least one of the required privileges.
 * @param {Array} userPrivileges - Array of user's privileges (objects with privilegeId).
 * @param {Array} requiredPrivileges - Array of privilege IDs required to access the content.
 * @returns {boolean} - True if the user has at least one of the required privileges, false otherwise.
 */

import { useAuth } from '../routes/authContext';
import PATHS from '../routes/paths';

export const checkAccess = (user, path, pathPrivileges) => {
  if (user?.roleLevel === 2) {
    // SuperAdmin has full access
    return true;
  }

  const requiredPrivileges = pathPrivileges[path];
  if (!requiredPrivileges) {
    // No privileges required for this path
    return true;
  }

  const userPrivileges = user?.privileges?.map((priv) => priv.privilegeId) || [];
  return requiredPrivileges.some((id) => userPrivileges.includes(id));
};

const privilegeConstants = {
  MANAGE_USER: [1],
  UPLOAD_VIDEO: [61, 62],
  EDIT_ECCLESIAL_DOCUMENT: [161],
  VIEW_ECCLESIAL_DOCUMENT: [160],
  DELETE_VIDEO: [61, 62],
  WATCH_WEBCAST: [200, 20],
  VIEW_RESOURCE: [120],
  EDIT_RESOURCE: [121],
  EXPORT_USAGE: [242],
  VIEW_WEBCAST_USAGE: [240, 241],
  PUBLIC_EVENTS_TAB: [1],
  VIEW_SYLLABUS: [180],
  EDIT_SYLLABUS: [181],
  ADD_SYLLABUS: [182],
  EXPORT_SYLLABUS: [184],
  DOWNLOAD_ANNOUCMENT: [183],
  EDIT_PUBLIC_EVENT: [261],
  VIEW_PUBLIC_EVENT: [260],
  VIEW_BIBLE_SEMINARS: [100],
  ADD_BIBLE_SEMINARS: [101],
  START_WEBCAST: [41, 42],
  // Add more privilege constants as needed
};

export const PATH_PRIVILEGES = {
  [PATHS.USERS_TAB]: privilegeConstants.MANAGE_USER,
  [PATHS.USAGE_TAB]: privilegeConstants.VIEW_WEBCAST_USAGE,
  [PATHS.PUBLIC_EVENTS_TAB]: privilegeConstants.PUBLIC_EVENTS_TAB,
  [PATHS.SYLLABUS]: privilegeConstants.VIEW_SYLLABUS,
  [PATHS.PUBLIC_EVENTS]: privilegeConstants.VIEW_PUBLIC_EVENT,
  [PATHS.PUBLIC_EVENTS_TAB]: privilegeConstants.EDIT_PUBLIC_EVENT,
  [PATHS.ADMIN_BIBLE_SEMINARS]: privilegeConstants.ADD_BIBLE_SEMINARS,
  // [PATHS.BIBLE_SEMINARS]: privilegeConstants.VIEW_BIBLE_SEMINARS,
  [PATHS.ECCLESIAL_DOCUMENT]: privilegeConstants.VIEW_ECCLESIAL_DOCUMENT,

  // Add more path-privilege mappings as needed
};

export const hasPrivilege = (userPrivileges, requiredPrivileges) => {
  if (!userPrivileges || !Array.isArray(userPrivileges)) return false;

  const privilegeIds = userPrivileges?.map((privilege) => privilege.privilegeId);
  return requiredPrivileges.some((privilegeId) => privilegeIds.includes(privilegeId));
};

export const usePrivileges = () => {
  const { user } = useAuth();
  const userPrivileges = user?.privileges || [];

  const canUpload = hasPrivilege(userPrivileges, privilegeConstants.UPLOAD_VIDEO);
  const canManageUsers = hasPrivilege(userPrivileges, privilegeConstants.MANAGE_USER);
  const canViewWebcastUsage = hasPrivilege(userPrivileges, privilegeConstants.VIEW_WEBCAST_USAGE);
  const canUploadEcclesialDocument = hasPrivilege(userPrivileges, privilegeConstants.EDIT_ECCLESIAL_DOCUMENT);
  const canDeleteVideo = hasPrivilege(userPrivileges, privilegeConstants.DELETE_VIDEO);
  const canWatchWebcast = hasPrivilege(userPrivileges, privilegeConstants.WATCH_WEBCAST);
  const viewResources = hasPrivilege(userPrivileges, privilegeConstants.VIEW_RESOURCE);
  const editResources = hasPrivilege(userPrivileges, privilegeConstants.EDIT_RESOURCE);
  const canExportUsage = hasPrivilege(userPrivileges, privilegeConstants.EXPORT_USAGE);
  const canEditSyllabus = hasPrivilege(userPrivileges, privilegeConstants.EDIT_SYLLABUS);
  const canAddSyllabus = hasPrivilege(userPrivileges, privilegeConstants.ADD_SYLLABUS);
  const canExportSyllabus = hasPrivilege(userPrivileges, privilegeConstants.EXPORT_SYLLABUS);
  const canDownloadAnnouncment = hasPrivilege(
    userPrivileges,
    privilegeConstants.DOWNLOAD_ANNOUCMENT
  );
  const canViewPublicEvents = hasPrivilege(userPrivileges, privilegeConstants.VIEW_PUBLIC_EVENT);
  const canEditPublicEvents = hasPrivilege(userPrivileges, privilegeConstants.EDIT_PUBLIC_EVENT);
  const canAddBibleSeminars = hasPrivilege(userPrivileges, privilegeConstants.ADD_BIBLE_SEMINARS);
  const canViewBibleSeminars = hasPrivilege(userPrivileges, privilegeConstants.VIEW_BIBLE_SEMINARS);
  const canStartWebcast = hasPrivilege(userPrivileges, privilegeConstants.START_WEBCAST);

  return {
    canUpload,
    canDeleteVideo,
    canWatchWebcast,
    editResources,
    viewResources,
    canUploadEcclesialDocument,
    canExportUsage,
    canManageUsers,
    canViewWebcastUsage,
    canEditSyllabus,
    canAddSyllabus,
    canExportSyllabus,
    canViewPublicEvents,
    canEditPublicEvents,
    canDownloadAnnouncment,
    canAddBibleSeminars,
    canViewBibleSeminars,
    canStartWebcast
  };
};
